import React from 'react';
import Grid from '@material-ui/core/Grid';
import { withStyles, WithStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import format from '../../../utils/formatter';
import DeleteIcon from '@material-ui/icons/DeleteOutline';
import { RouteComponentProps } from 'react-router-dom';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import NoRecord from '../NoRecord';
import { DatePicker } from 'material-ui-pickers';
import Delete from '@material-ui/icons/Delete';
import Filter from '@material-ui/icons/FilterList';
import IconButton from '@material-ui/core/IconButton';
import { withNamespaces, WithNamespaces } from 'react-i18next';
import './history.scss';
import CircularProgress from '@material-ui/core/CircularProgress';
import { connect } from 'react-redux';
import { MotorcycleModelResponse, Price } from '../motorcycles/MotorcycleView';
import { classes } from './historyStyles';
import store from '../../../redux/store';
import load from '../../../redux/async-actions/loadConfiguration';
import Left from '@material-ui/icons/KeyboardArrowLeft';
import Right from '@material-ui/icons/KeyboardArrowRight';
import { TextField } from '@material-ui/core';
import http from '../../../services/http';
import { ShowDialogProps } from '../../../redux/actions';
import { DialogTypes } from '../../../enums/dialogTypes';
import { StateManagementProps, mapDispatchToProps, mapStateToProps } from '../../../utils/addReduxProps';
import DialogRouter from '../../dialogs/dialogRouter';
import { AxiosResponse } from 'axios';
import Toast from '../../shared/Toast';
import { toastMessage } from '../../../utils/helpers';

export interface HistoryResponseItem {
	Configurations: Configuration[];
}

export interface Configuration {
	Id: number;
	Model: Model;
	TotalPrice: Price;
	Customer: Customer;
	Comment: string;
	SavedOn: number;
}

export interface Customer {
	Lastname: string;
	Firstname: string;
	Fullname?: string;
}

export interface Model {
	HeroImageUrl: string;
	Id: string;
	Name: string;
}

export interface HistoryViewProps {
	models: MotorcycleModelResponse;
}


export interface HistoryViewState {
	items: Configuration[];
	modelName: string;
	dateFrom: number | null;
	dateTo: number | null;
	nameFilter: string;
	filter: boolean;
	pager: PagerProps;
	loaded: boolean;
}


export interface PagerProps {
	currentPage: number;
	perPage: number;
}

const perPageResultcount = 10;

const pagerInitialState: PagerProps = {
	currentPage: 0,
	perPage: perPageResultcount,
}

const initialState = {
	items: [],
	modelName: '',
	dateFrom: 0,
	dateTo: Date.now(),
	nameFilter: '',
	filter: false,
	pager: pagerInitialState,
	loaded: false,
};

export type HistoryAllProps = HistoryViewProps & WithStyles & RouteComponentProps<any> & WithNamespaces & StateManagementProps;

export class HistoryView extends React.Component<HistoryAllProps, HistoryViewState> {
	constructor(props: HistoryAllProps) {
		super(props);
		this.state = { ...initialState };
		this.filterRows = this.filterRows.bind(this);
		this.handleModelChange = this.handleModelChange.bind(this);
		this.handleDateChange = this.handleDateChange.bind(this);
		this.clearFilter = this.clearFilter.bind(this);
		this.showConfirm = this.showConfirm.bind(this);
		this.handleNameFilterChange = this.handleNameFilterChange.bind(this);
		this.updateHistoryState = this.updateHistoryState.bind(this);
		this.loadData = this.loadData.bind(this);
		this.deleteItemAndReload = this.deleteItemAndReload.bind(this);
	}

	componentDidMount() {
		this.loadData();
	}

	loadData() {
		return http.getHistory()
			.then(this.updateHistoryState)
			.catch(error => console.log(error));
	}

	deleteItemAndReload(id: number) {
		http.deleteConfiguration(id)
			.then(() => {
				toastMessage(this.props.t('configuration deleted'));
			})
			.then(this.loadData);
	}

	updateHistoryState(response: AxiosResponse<HistoryResponseItem>) {
		const { Configurations } = response.data;
		if (Configurations.length) {
			this.setState({
				items: Configurations.map((configuration: Configuration) => {
					const { Customer: { Firstname, Lastname } } = configuration;
					return {
						...configuration,
						Customer: {
							...configuration.Customer,
							Fullname: `${Firstname} ${Lastname}`,
						}
					}
				}),
				...this.setDates(Configurations),
				loaded: true,
			});
		} else {
			this.setState({ loaded: true })
		}
	}

	handleModelChange(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
		const { name, value } = e.target;
		this.setState({
			...this.state,
			filter: true,
			[name]: value,
		});

		this.setPage(0);
	}

	handleDateChange(controlName: string, value: Date) {
		this.setState({
			...this.state,
			filter: true,
			[controlName]: value.setHours(0, 0, 0, 0),
		});

		this.setPage(0);
	}

	handleNameFilterChange(nameFilter: string) {
		this.setState({ nameFilter, filter: true });
		this.setPage(0);
	}

	clearFilter() {
		this.setState({ ...initialState, items: [...this.state.items], ...this.setDates(this.state.items) });
	}

	setPage(currentPage: number) {
		this.setState({ pager: { ...this.state.pager, currentPage } })
	}

	filterRows(item: Configuration): boolean {
		return this.fitsModel(item) && this.fitsFrom(item) && this.fitsTo(item) && this.fitsName(item);
	}

	showConfirm(id: number, e: React.MouseEvent<HTMLElement>) {
		e.stopPropagation();

		const dialogProps: ShowDialogProps<{ text: string, deleteAndReloadHistory: (id: number) => void, id: number }> = {
			title: this.props.t('dialogs.deleteConfiguration.title'),
			data: {
				id,
				text: this.props.t('dialogs.deleteConfiguration.text'),
				deleteAndReloadHistory: this.deleteItemAndReload,
			},
			contentType: DialogTypes.deleteConfiguraion,
		};
		this.props.showDialog(dialogProps);
	}

	private fitsModel(item: Configuration): boolean {
		return !this.state.modelName || this.state.modelName === item.Model.Name;
	}

	private fitsName(item: Configuration): boolean {
		return !this.state.nameFilter || item.Customer['Fullname']!.toLowerCase().includes(this.state.nameFilter.toLowerCase().trim());
	}


	private fitsFrom(item: Configuration): boolean {
		return !this.state.dateFrom || this.state.dateFrom <= item.SavedOn;
	}

	private fitsTo(item: Configuration): boolean {
		return !this.state.dateTo || this.state.dateTo >= item.SavedOn;
	}

	private setDates(items: Configuration[]) {
		return {
			dateFrom: Math.min(...items.map(item => item.SavedOn)),
			dateTo: Math.max(...items.map(item => item.SavedOn)),
		}
	}

	render() {

		const { t, toast } = this.props;
		const {
			filter,
			table,
			imagePreview,
			deleteClass,
			selectRoot,
			gap,
			noRecord,
			tableHeaderCell,
			tableBodyCell,
			imageCell,
			filterNameRoot,
			tableRow,
		} = this.props.classes;

		const minDateProps = {
			className: gap,
			disableFuture: true,
			format: 'dd.MM.yyyy',
			maxDate: this.state.dateTo && new Date(this.state.dateTo),
			label: t('history.filters.dateFrom'),
			value: this.state.dateFrom,
			onChange: this.handleDateChange.bind(null, 'dateFrom'),
		}
		const maxDateProps = {
			className: gap,
			disableFuture: true,
			format: 'dd.MM.yyyy',
			minDate: this.state.dateFrom && new Date(this.state.dateFrom),
			label: t('history.filters.dateTo'),
			value: this.state.dateTo,
			onChange: this.handleDateChange.bind(null, 'dateTo'),
		}

		const modelNames: string[] = this.state.items
			.map((item: Configuration) => item.Model.Name)
			.reduce((acc: string[], modelName: string) => acc.includes(modelName) ? acc : [...acc, modelName], [])
			.sort();

		const filtredItems = this.state.items.filter(this.filterRows);
		const { currentPage } = this.state.pager;


		const pageArray = Array.from(Array(Math.ceil(filtredItems.length / perPageResultcount)).keys())
		const Pager = filtredItems.length > perPageResultcount &&
			<section id="history-pager" className="flex-container centered">
				<div>
					{/* Previous page */}
					<span
						className="clickable pager-item arrow"
						onClick={() => currentPage !== 0 && this.setPage(currentPage - 1)}>
						<Left />
					</span>
					{pageArray.map((item, index: number) => <span key={index}
						onClick={() => this.setPage(index)}
						className={`clickable pager-item ${index === currentPage ? 'selected' : ''}`}>{index + 1}
					</span>)}
					{/* Next page */}
					<span
						className="clickable pager-item arrow"
						onClick={() => currentPage !== pageArray.length - 1 && this.setPage(this.state.pager.currentPage + 1)}>
						<Right />
					</span>
				</div>
			</section>;


		const tableItems = filtredItems
			// pager filter
			.filter((row: Configuration, index) => {
				const { currentPage, perPage } = this.state.pager;
				return index >= currentPage * perPage && index < (currentPage + 1) * perPage
			})
			// latest first
			.sort((a, b) => b.SavedOn - a.SavedOn)
			.map((row: Configuration, index: number) => (
					<TableRow className={tableRow} key={index} onClick={() => store.dispatch(load([row, this.props.history]) as any)}>
						<TableCell padding="none" classes={{ body: tableBodyCell, root: imageCell }} scope="row">
							<img className={imagePreview} src={row.Model.HeroImageUrl} alt="hero" />
						</TableCell>
						<TableCell classes={{ body: tableBodyCell }} align="left">{row.Model.Name}</TableCell>
						<TableCell classes={{ body: tableBodyCell }} align="left">{format.currency(row.TotalPrice)}</TableCell>
						<TableCell classes={{ body: tableBodyCell }} align="left">{`${row.Customer.Firstname} ${row.Customer.Lastname}`}</TableCell>
						<TableCell classes={{ body: tableBodyCell }} align="left">{`${row.Comment}`}</TableCell>
						<TableCell classes={{ body: tableBodyCell }} align="left">{format.toDateLocaleString(row.SavedOn)}</TableCell>
						<TableCell classes={{ body: tableBodyCell }} align="left">
							<IconButton onClick={this.showConfirm.bind(null, row.Id)}><DeleteIcon className={`clickable ${deleteClass}`} /></IconButton>
						</TableCell>
					</TableRow>
				));
		return (
			<React.Fragment>
				<Grid item className={filter}>
					<form autoComplete="off">
						<IconButton disabled={true}>
							<Filter fontSize="large" />
						</IconButton>
						<IconButton disabled={!this.state.filter} onClick={this.clearFilter} aria-label="Remove filter">
							<Delete fontSize="large" />
						</IconButton>

						<FormControl className={gap}>
							<InputLabel htmlFor="age-simple">{t('history.filters.modelName')}</InputLabel>
							<Select
								classes={{ root: selectRoot }}
								autoWidth={true}
								value={this.state.modelName}
								onChange={this.handleModelChange}
								inputProps={{
									name: 'modelName',
									id: 'modelName',
								}}
							>
								<MenuItem value={''}><em>{t('history.filters.modelName')}</em></MenuItem>
								{modelNames.map((name: string, key) => (
									<MenuItem key={key} value={name}>{name}</MenuItem>
								))}
							</Select>
						</FormControl>
						<FormControl className={gap}>
							<TextField
								id="fullname-filter"
								label={t('history.filters.fullname')}
								value={this.state.nameFilter}
								fullWidth={true}
								classes={{ root: filterNameRoot }}
								onChange={({ target: { value } }) => this.handleNameFilterChange(value)}
							/>
						</FormControl>
						<DatePicker {...minDateProps} />
						<DatePicker {...maxDateProps} />
					</form>
				</Grid>
				<Grid item className={table}>
					<Table id="history-table">
						<TableHead>
							<TableRow>
								<TableCell classes={{ root: imageCell }} align="left" padding="none">{/*picture*/}</TableCell>
								{['model', 'price', 'customer', 'comment', 'date', 'actions'].map((item, idx) => (
									<TableCell key={idx} classes={{ head: tableHeaderCell }} align="left">
										{t(`history.header.${item}`)}
									</TableCell>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							{tableItems}
						</TableBody>
						{tableItems.length === 0 &&
							<TableBody>
								<TableRow className="no-record">
									<TableCell colSpan={7}>
										<NoRecord className={noRecord}>
											{this.state.loaded ? <p className="no-record-text">{t('history.message.noRecord')}</p> : <CircularProgress />}
										</NoRecord>
									</TableCell>
								</TableRow>
							</TableBody>}
					</Table>
					{/* pager */}
					{Pager}
				</Grid>
				<DialogRouter />
				<Toast  {...toast} />
			</React.Fragment>
		);
	}
}
export default connect(mapStateToProps, mapDispatchToProps)(withNamespaces()(withStyles(classes)(HistoryView)));
